<template>
  <div :class="isToaster ? $style.container__toaster : $style.container__main">
    <template
      v-for="(notificationTable, index) in notifications"
      :key="index"
    >
      <!-- Cancellation: one notification per item, special link/message/emit -->
      <template v-if="notificationTable.length && isCancellationType(notificationTable)">
        <NotificationContent
          v-for="(notification, i) in notificationTable"
          :id="notification.orderId"
          :key="notification.orderId + '-' + i"
          :type="notification.type.toLowerCase()"
          :icon="notification.type.toLowerCase()"
          :is-read="false"
          :label="`1 ` + $t(`notification.label.${notification.type.toLowerCase()}`)"
          :length="1"
          :link="''"
          :message="getIndividualMessage(notification)"
          :need-close="needClose"
          :order-number="notification.orderNumber"
          @processed-cancel-order="onProcessedCancelOrder($event)"
          @dismiss="
            onDismiss(notificationTable);
            onNewDismiss(notificationTable[0].type.toLowerCase());
          "
        />
      </template>

      <!-- All other types: grouped notification -->
      <NotificationContent
        v-else-if="notificationTable.length"
        :id="notificationTable[0].orderId"
        :type="notificationTable[0].type.toLowerCase()"
        :icon="notificationTable[0].type.toLowerCase()"
        :is-read="false"
        :label="setLabel(notificationTable[0].type.toLowerCase(), notificationTable.length)"
        :length="notificationTable.length"
        :link="
          toLink(
            router,
            notificationTable.length,
            notificationTable[0].orderNumber,
            notificationTable[0].type.toLowerCase()
          )
        "
        :message="setMessage(notificationTable[0].type.toLowerCase())"
        :need-close="needClose"
        :order-number="notificationTable[0].orderNumber"
        @processed-cancel-order="onClickGroupNotification(notificationTable)"
        @dismiss="
          onDismiss(notificationTable);
          onNewDismiss(notificationTable[0].type.toLowerCase());
        "
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import type { PFSNotification } from '~/app/types/notificationType';
import { toLink } from '~/composables/notifications/useNotifications';

type Props = {
  isToaster: boolean;
  needClose?: boolean;
  notifications?: PFSNotification[][]; // TODO BB-975 Is the [][] on purpose ?
};

withDefaults(defineProps<Props>(), {
  needClose: true,
  notifications: () => [[]],
});

const router = useRouter();
const i18n = useI18n();

const onNewDismiss = (notificationType: any) => {
  emits('dismissed-notification', notificationType);
};

const emits = defineEmits([
  'dismissed-order',
  'dismissed-notification',
  'processed-order',
  'processed-cancel-order',
  'mark-all-as-read',
]);

const tableOfDismissOrders: any = ref([]);

const onDismiss = (event: any) => {
  tableOfDismissOrders.value = [];
  tableOfDismissOrders.value.push(event);
  emits('dismissed-order', tableOfDismissOrders.value);
};

const onProcessedCancelOrder = (orderId: string) => {
  emits('processed-cancel-order', orderId);
};

const isCancellationType = (notificationTable: PFSNotification[]) => {
  const type = notificationTable[0].type.toLowerCase();
  return type === 'cancellation' || type === 'refused' || type === 'expired';
};

const setLabel = (type: string, length: number) => {
  const suffix = length === 1 ? type : `${type}Plural`;
  return `${length} ` + i18n.t(`notification.label.${suffix}`);
};

const setMessage = (type: string) => {
  return i18n.t(`notification.message.${type}Plural`);
};

const getIndividualMessage = (notification: PFSNotification) => {
  const type = notification.type.toLowerCase();
  if (type === 'refused') {
    return i18n.t('notification.message.refused');
  } else {
    return i18n.t('notification.message.hideNotification');
  }
};

const onClickGroupNotification = (notificationTable: PFSNotification[]) => {
  emits('mark-all-as-read', notificationTable);
  onNewDismiss(notificationTable[0].type.toLowerCase());
};
</script>

<style lang="postcss" module>
.container {
  &__toaster {
    position: fixed;
    right: 80px;
    top: 80px;
    z-index: 40;
    @media (max-width: 900px) {
      right: 10px;
    }
    @media (max-width: 450px) {
      display: flex;
      justify-content: flex-end;
      max-width: 300px;
      right: 10px;
      width: 100%;
      flex-direction: column;
    }
  }
  &__main {
    position: relative;
  }
}
</style>
